import { Building2, Globe, Shield } from 'lucide-react';
import { ProductActionMenu, type ProductAction } from './products/ProductActionMenu';
import { Card } from './ui/card';
import { CardCarousel } from './ui/card-carousel';
import { type DisplayCrop } from './ui/product-image';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { cn } from '@/lib/utils';

export function ProductCard({
	name,
	sku,
	images,
	tags,
	organization,
	showOwner = true,
	isPublishedToCommunity = false,
	isSelected = false,
	onSelect,
	onClick,
	actions,
}: {
	name: string;
	sku?: string | null;
	images?: Array<{
		url?: string | null;
		isPrimary?: boolean | null;
		crop?: DisplayCrop | null;
	}> | null;
	tags?: Array<{ name: string }> | null;
	organization?: { id: string; name: string; logo?: string | null } | null;
	/** Off where every card has the same owner, e.g. on that client's own page. */
	showOwner?: boolean;
	/** Marks a house product that is on the community page. */
	isPublishedToCommunity?: boolean;
	isSelected?: boolean;
	/** Omit to render the card without a selection checkbox. */
	onSelect?: () => void;
	onClick: () => void;
	/** The "…" menu. Omit (or pass none) for a card without one. */
	actions?: ProductAction[];
}) {
	return (
		<Card
			className={cn(
				'group cursor-pointer overflow-hidden bg-surface gap-0 py-0 transition-all duration-[180ms]',
				isSelected
					? 'border-accent ring-1 ring-accent'
					: 'border-line-1 hover:border-line-2'
			)}
			onClick={onClick}
		>
			{/* Image area */}
			<div
				className="relative overflow-hidden border-b border-line-1 bg-media-well"
				style={{ aspectRatio: '1' }}
			>
				<CardCarousel images={images} name={name} />

				{/* Top-left overlays: owner badge, then tag */}
				<div className="absolute top-2.5 left-2.5 z-10 flex max-w-[calc(100%-3rem)] flex-col items-start gap-1.5">
					{/* One owner badge. A community product is always internal, so Community
					    replaces Internal rather than stacking on it. */}
					{isPublishedToCommunity ? (
						<Badge
							className="flex items-center gap-1 bg-accent-soft text-xs text-accent backdrop-blur-sm"
							title="Internal product on the community page"
						>
							<Globe size={11} className="shrink-0" />
							Community
						</Badge>
					) : !showOwner ? null : organization ? (
						<Badge
							className="flex items-center gap-1 bg-status-neutral-soft text-xs text-text-2 backdrop-blur-sm"
							title={`Organization product — ${organization.name}`}
						>
							{organization.logo ? (
								<img
									src={organization.logo}
									alt=""
									className="h-3.5 w-3.5 shrink-0 rounded-full object-cover"
								/>
							) : (
								<Building2 size={11} className="shrink-0" />
							)}
							<span className="max-w-[130px] truncate">{organization.name}</span>
						</Badge>
					) : (
						<Badge
							className="flex items-center gap-1 bg-status-neutral-soft text-xs text-text-3 backdrop-blur-sm"
							title="Internal product — not assigned to a client"
						>
							<Shield size={11} className="shrink-0" />
							Internal
						</Badge>
					)}
					{tags && tags.length > 0 && (
						<Badge className="bg-status-neutral-soft text-xs text-text-3 backdrop-blur-sm">
							{tags[0].name}
						</Badge>
					)}
				</div>

				{/* Selection checkbox */}
				{onSelect && (
					<div
						className="absolute top-2.5 right-2.5 z-10"
						onClick={(e) => e.stopPropagation()}
					>
						<Checkbox
							variant="overlay"
							checked={isSelected}
							onCheckedChange={onSelect}
						/>
					</div>
				)}
			</div>

			{/* Info */}
			<div className="px-3.5 pt-3 pb-3.5">
				<div className="mb-1.5 truncate text-base font-medium leading-snug text-text-1">
					{name}
				</div>
				<div className="flex min-h-5 items-center justify-between gap-2">
					<span className="truncate font-mono text-xs text-text-4">{sku || ' '}</span>
					{actions && <ProductActionMenu actions={actions} className="-my-1" />}
				</div>
			</div>
		</Card>
	);
}
